import { useEffect, useRef, useState } from "react";
import { ChevronDown, Copy, KeyRound } from "lucide-react";
import { AccountKeyResponseSchema, RevokeAccountKeyResponseSchema } from "@pakker/shared";
import { apiRequest } from "../../api/client.js";
import { Button } from "../../ui/button.js";
import { agentPlanningPrompt } from "./agent-prompt.js";
import "./account-key-controls.css";

export function AccountKeyControls({ orgId }: { orgId: string }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualPrompt, setManualPrompt] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const manualCopy = useRef<HTMLTextAreaElement>(null);
  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    if (manualPrompt) {
      manualCopy.current?.focus();
      manualCopy.current?.select();
    }
  }, [manualPrompt]);

  async function manage(action: "copy" | "rotate" | "revoke") {
    if (busy) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setMessage(null);
    setError(null);
    setManualPrompt(null);
    try {
      if (action === "revoke") {
        await apiRequest("/api/me/account-key", {
          schema: RevokeAccountKeyResponseSchema,
          method: "DELETE",
          orgId,
          signal: controller.signal,
        });
        if (!controller.signal.aborted)
          setMessage("Key revoked. Agents using it can no longer access your plans.");
        return;
      }
      const response = await apiRequest(
        `/api/me/account-key${action === "rotate" ? "/rotate" : ""}`,
        {
          schema: AccountKeyResponseSchema,
          method: "POST",
          body: {},
          orgId,
          signal: controller.signal,
        },
      );
      if (controller.signal.aborted) return;
      const prompt = agentPlanningPrompt(window.location.origin, response.key);
      try {
        await navigator.clipboard.writeText(prompt);
        if (!controller.signal.aborted)
          setMessage(
            action === "rotate"
              ? "Key rotated and prompt copied. The previous key no longer works."
              : "Prompt copied with your account key. Paste it into your agent with your packing list.",
          );
      } catch {
        if (!controller.signal.aborted) {
          setManualPrompt(prompt);
          setMessage(
            action === "rotate"
              ? "Key rotated. Clipboard unavailable; copy the new prompt below. The previous key no longer works."
              : "Clipboard unavailable. Copy the selected prompt below.",
          );
        }
      }
    } catch {
      if (!controller.signal.aborted)
        setError("Could not confirm the account key request. Try again.");
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  return (
    <section className="account-key-controls" aria-label="Account key for agents">
      <Button variant="ghost" disabled={busy} onClick={() => void manage("copy")}>
        <Copy size={18} aria-hidden="true" /> Copy Instructions for AI
      </Button>
      {message ? <p role="status">{message}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {manualPrompt ? (
        <label className="account-key-fallback">
          Agent prompt to copy
          <textarea
            ref={manualCopy}
            readOnly
            value={manualPrompt}
            rows={7}
            onFocus={(event) => event.currentTarget.select()}
          />
        </label>
      ) : null}
      <details>
        <summary>
          <KeyRound size={18} aria-hidden="true" />
          Manage account key
          <ChevronDown className="account-key-chevron" size={16} aria-hidden="true" />
        </summary>
        <p className="account-key-note">
          Rotation replaces your key. Revocation stops agents using it. Keep the copied prompt
          private.
        </p>
        <Button variant="ghost" disabled={busy} onClick={() => void manage("rotate")}>
          Rotate and copy key
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => void manage("revoke")}>
          Revoke key
        </Button>
      </details>
    </section>
  );
}
